/**
 * DrillDownPanel — slide-in right panel for "show me why" details
 * (PR F · §F.1.3).
 *
 * Open/close via the `open` prop; ESC + close button both invoke
 * `onClose`. Renders inside a `role="dialog"` so screen readers
 * announce it as a modal surface. Stacks above WorkspaceSheet
 * (z-index: 30) but below true modals (z-index: 40).
 */
import type { JSX, ReactNode } from 'react';
import { useEffect } from 'react';
import { Icon } from './Icon.js';

export interface DrillDownPanelProps {
  open: boolean;
  title: string;
  subtitle?: string;
  footer?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
}

export function DrillDownPanel({
  open,
  title,
  subtitle,
  footer,
  onClose,
  children,
}: DrillDownPanelProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="drill-down-panel"
      role="dialog"
      aria-label={title}
    >
      <header className="drill-down-panel-head">
        <div>
          <h6 className="drill-down-panel-title">{title}</h6>
          {subtitle && <p className="drill-down-panel-sub">{subtitle}</p>}
        </div>
        <button
          type="button"
          className="drill-down-panel-close"
          onClick={onClose}
          aria-label="Close"
        >
          <Icon name="close" size="sm" />
        </button>
      </header>
      <div className="drill-down-panel-body">{children}</div>
      {footer && <footer className="drill-down-panel-foot">{footer}</footer>}
    </div>
  );
}
